import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { usePermission } from '../../components/permissions/PermissionTooltip';
import { onSnapshot } from 'firebase/firestore';
import { httpsCallable } from '../../lib/callable';
import { functions } from '../../firebase';
import { CLOUD_FUNCTIONS, CALLABLE_TIMEOUTS } from '../../constants/firebase';
import { artifactsCollection, essentialDocsCollection } from '../../constants/firestorePaths';
import {
  entryName,
  filterZoneGroups,
  groupEntriesByZone,
  locatedInLabel,
  statusBadgeClasses,
  statusLabel,
  summarizeChecklist,
  TMF_ZONES_VIEW,
  type TmfChecklistEntryView,
} from '../../utils/tmfChecklistView';
import {
  FileText,
  CheckCircle2,
  Clock,
  Loader2,
  Plus,
  Search,
  ChevronRight,
  ChevronDown,
  File,
  FileCheck,
  Folder,
  Eye
} from 'lucide-react';

/**
 * A checklist entry, exactly as `initializeTMFHandler` writes it.
 *
 * The field is `name`, not `documentName`, and the status vocabulary is the
 * server's four — see `utils/tmfChecklistView.ts` for the history of getting
 * both of those wrong here.
 */
interface EssentialDoc extends TmfChecklistEntryView {
  id: string;
}

const documentCategories = [
  { id: 'before_trial', name: 'Before Trial Initiation', icon: Clock },
  { id: 'during_trial', name: 'During Trial Conduct', icon: FileText },
  { id: 'after_trial', name: 'After Trial Completion', icon: CheckCircle2 }
];

export default function Documents() {
  const { projectId } = useParams<{ projectId: string }>();
  const canGenerate = usePermission('generate');
  const [activeTab, setActiveTab] = useState<'tmf' | 'essential' | 'generated'>('tmf');
  const [loading, setLoading] = useState(true);
  const [essentialDocs, setEssentialDocs] = useState<EssentialDoc[]>([]);
  const [generatedDocs, setGeneratedDocs] = useState<any[]>([]);
  const [generating, setGenerating] = useState(false);
  const [expandedZones, setExpandedZones] = useState<Set<string>>(
    () => new Set(TMF_ZONES_VIEW.slice(0, 2).map(z => z.id)),
  );
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!projectId) return;

    // Load essential docs
    const unsubEssential = onSnapshot(
      essentialDocsCollection(projectId),
      (snapshot) => {
        const docs: EssentialDoc[] = [];
        snapshot.forEach((doc) => {
          docs.push({ id: doc.id, ...doc.data() } as EssentialDoc);
        });
        setEssentialDocs(docs);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading essential docs:', error);
        setLoading(false);
      }
    );

    // Load generated artifacts that are documents
    const unsubArtifacts = onSnapshot(
      artifactsCollection(projectId),
      (snapshot) => {
        const docs: any[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (['user_spec_doc', 'functional_requirements', 'data_design_spec', 'db_build_guide', 'validation_plan'].includes(data.type)) {
            docs.push({ id: doc.id, ...data });
          }
        });
        setGeneratedDocs(docs);
      }
    );

    return () => {
      unsubEssential();
      unsubArtifacts();
    };
  }, [projectId]);

  const handleGenerateDocument = async (docType: string) => {
    setGenerating(true);
    try {
      // Gen-2 spec-document generator (generateSpecDocumentV2). The handler
      // takes exactly { projectId, docType } and routes docType through the
      // DocumentationAgent, which supports all five docTypes the buttons send
      // (user_spec_doc, functional_requirements, data_design_spec,
      // db_build_guide, validation_plan). GENERATOR_XLARGE matches the
      // function's 1800s server ceiling so slow generations don't trip the
      // browser default (~70s) with a premature deadline-exceeded.
      const generateFn = httpsCallable(
        functions,
        CLOUD_FUNCTIONS.GENERATE_SPEC_DOCUMENT_V2,
        { timeout: CALLABLE_TIMEOUTS.GENERATOR_XLARGE }
      );
      await generateFn({ projectId, docType });
    } catch (error) {
      console.error('Error generating document:', error);
    } finally {
      setGenerating(false);
    }
  };

  const toggleZone = (zoneId: string) => {
    const newExpanded = new Set(expandedZones);
    if (newExpanded.has(zoneId)) {
      newExpanded.delete(zoneId);
    } else {
      newExpanded.add(zoneId);
    }
    setExpandedZones(newExpanded);
  };

  // Counts and zone grouping both come from `utils/tmfChecklistView.ts`, which
  // the Essential Documents page reads too — one vocabulary, tested against the
  // server's own, rather than a private copy per page.
  const checklist = summarizeChecklist(essentialDocs);
  const zoneGroups = filterZoneGroups(groupEntriesByZone(essentialDocs), searchQuery);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documents & TMF</h1>
          <p className="text-gray-600">Manage trial master file and essential documents</p>
        </div>
        {/* The GCP essential-documents checklist — and, since 2026-08-24, the
            files each entry holds — lives on its own page. It was routed at
            /project/:id/tmf but linked only from a page that is no longer in
            the router, so nothing in the running app could reach it. */}
        <Link
          to={`/project/${projectId}/tmf`}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
        >
          <FileCheck className="h-4 w-4" />
          <span>Essential Documents checklist</span>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-gray-600">Completion</p>
            <span className="text-sm font-medium text-primary-600">{checklist.completionPercent}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all"
              style={{ width: `${checklist.completionPercent}%` }}
            ></div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Complete</p>
              <p className="text-2xl font-bold text-gray-900">{checklist.complete}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-gray-900">{checklist.inProgress}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Generated</p>
              <p className="text-2xl font-bold text-gray-900">{generatedDocs.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'tmf', name: 'Trial Master File', icon: <Folder className="h-4 w-4" /> },
            { id: 'essential', name: 'Essential Documents', icon: <FileCheck className="h-4 w-4" /> },
            { id: 'generated', name: 'Generated Specs', count: generatedDocs.length, icon: <FileText className="h-4 w-4" /> }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              <span>{tab.name}</span>
              {tab.count !== undefined && (
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="card">
        {/* TMF Tab */}
        {activeTab === 'tmf' && (
          <div>
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search zones and documents..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
                />
              </div>
            </div>

            <div className="divide-y divide-gray-100">
              {zoneGroups.map((zone) => {
                const isExpanded = expandedZones.has(zone.id);

                return (
                  <div key={zone.id}>
                    <button
                      onClick={() => toggleZone(zone.id)}
                      className="w-full px-4 py-4 flex items-center justify-between hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-4">
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                        <div className="h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <Folder className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-gray-900">{zone.name}</h3>
                          <p className="text-sm text-gray-500">{zone.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className="text-sm text-gray-500">
                          {zone.summary.complete} of {zone.summary.total} complete
                        </span>
                        <div className="flex items-center space-x-1">
                          <div className="h-2 w-16 bg-gray-200 rounded-full">
                            <div
                              className="h-2 bg-green-500 rounded-full"
                              style={{ width: `${zone.summary.completionPercent}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 pl-16">
                        {zone.entries.length > 0 ? (
                          <div className="space-y-2">
                            {zone.entries.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center space-x-3">
                                  <File className="h-4 w-4 text-gray-400" />
                                  <div>
                                    <p className="text-sm text-gray-900">{entryName(doc)}</p>
                                    {doc.description && (
                                      <p className="text-xs text-gray-500">{doc.description}</p>
                                    )}
                                  </div>
                                </div>
                                <span className={`px-2 py-1 text-xs rounded-full ${statusBadgeClasses(doc.status)}`}>
                                  {statusLabel(doc.status)}
                                </span>
                              </div>
                            ))}
                            {/* This zone's rows on the page that can set a status
                                and show the files each entry holds. The zone id is
                                the one EssentialDocs already filters on. */}
                            <Link
                              to={`/project/${projectId}/tmf?zone=${zone.id}`}
                              className="inline-flex items-center gap-1 pt-1 text-sm text-primary-600 hover:text-primary-700"
                            >
                              <FileCheck className="h-4 w-4" />
                              <span>Open this zone in Essential Documents</span>
                            </Link>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <File className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm">No documents in this zone yet</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {zoneGroups.length === 0 && (
                <div className="px-4 py-10 text-center text-gray-500 text-sm">
                  No zones or documents match &ldquo;{searchQuery}&rdquo;.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Essential Documents Tab */}
        {activeTab === 'essential' && (
          <div>
            {documentCategories.map((category) => {
              const CategoryIcon = category.icon;
              const categoryDocs = essentialDocs.filter(d => d.category === category.id);

              return (
                <div key={category.id} className="border-b border-gray-100 last:border-0">
                  <div className="px-4 py-3 bg-gray-50 flex items-center space-x-3">
                    <CategoryIcon className="h-5 w-5 text-gray-600" />
                    <h3 className="font-semibold text-gray-900">{category.name}</h3>
                    <span className="text-sm text-gray-500">({categoryDocs.length} documents)</span>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {categoryDocs.map((doc) => (
                      <div key={doc.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex items-center space-x-3">
                          <File className="h-4 w-4 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{entryName(doc)}</p>
                            <p className="text-xs text-gray-500">{doc.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${statusBadgeClasses(doc.status)}`}>
                            {statusLabel(doc.status)}
                          </span>
                          <span className="text-xs text-gray-500">{locatedInLabel(doc.locatedIn)}</span>
                        </div>
                      </div>
                    ))}
                    {categoryDocs.length === 0 && (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">
                        No documents in this category
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Generated Specs Tab */}
        {activeTab === 'generated' && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                { type: 'user_spec_doc', name: 'User Specification Document', desc: 'System requirements and user roles' },
                { type: 'functional_requirements', name: 'Functional Requirements', desc: 'EDC system functional specifications' },
                { type: 'data_design_spec', name: 'Data Design Specification', desc: 'Database architecture and schema' },
                { type: 'db_build_guide', name: 'Database Build Guide', desc: 'Setup and configuration instructions' },
                { type: 'validation_plan', name: 'Validation Plan', desc: 'Testing and verification procedures' }
              ].map((docType) => {
                const existingDoc = generatedDocs.find(d => d.type === docType.type);

                return (
                  <div key={docType.type} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{docType.name}</h3>
                        <p className="text-sm text-gray-500">{docType.desc}</p>
                      </div>
                      {existingDoc && (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                    {existingDoc ? (
                      <div className="flex items-center space-x-2">
                        <button className="flex-1 px-3 py-2 text-sm bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 flex items-center justify-center space-x-1">
                          <Eye className="h-4 w-4" />
                          <span>View</span>
                        </button>
                        <button
                          onClick={() => handleGenerateDocument(docType.type)}
                          disabled={generating || !canGenerate}
                          title={canGenerate ? undefined : "Your role doesn't allow regenerating documents."}
                          className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Regenerate
                        </button>
                      </div>
                    ) : !canGenerate ? (
                      <div className="w-full px-3 py-2 text-sm rounded-lg bg-slate-50 text-slate-600 text-center ring-1 ring-inset ring-slate-200">
                        Not yet generated
                      </div>
                    ) : (
                      <button
                        onClick={() => handleGenerateDocument(docType.type)}
                        disabled={generating}
                        className="w-full px-3 py-2 text-sm btn-primary disabled:opacity-50 flex items-center justify-center space-x-1"
                      >
                        {generating ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                        <span>Generate</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
